import type { SubscriptionPlan, SubscriptionInterval } from './entitlements';
import { UAE_TEAM_SEAT_USD_DEFAULT } from './uaeTeam';

export type StripePriceConfig = {
  memberMonthlyPriceId?: string;
  memberAnnualPriceId?: string;
  proMonthlyPriceId?: string;
  proAnnualPriceId?: string;
  teamAnnualPriceId?: string;
  teamExtraSeatAnnualPriceId?: string;
  // ID-241: UAE Programme Team plan, per seat per month (subscription quantity = seats).
  uaeTeamSeatMonthlyPriceId?: string;
  // Old Pro price ids retired by a reprice but kept ACTIVE in Stripe — they
  // still resolve to Pro so existing subscribers keep their access.
  legacyProPriceIds?: string[];
};
export type PaypalPlanConfig = {
  memberMonthlyPlanId?: string;
  memberAnnualPlanId?: string;
  proMonthlyPlanId?: string;
  proAnnualPlanId?: string;
  teamAnnualPlanId?: string;
  // Old Pro plan ids retired by a reprice but kept active in PayPal.
  legacyProPlanIds?: string[];
};
export type UsdDisplayPrices = {
  memberMonthly?: number;
  memberAnnual?: number;
  proMonthly?: number;
  proAnnual?: number;
  teamAnnual?: number;
  uaeTeamSeatMonthly?: number;
};
export type BillingPricesConfig = {
  stripe?: StripePriceConfig;
  paypal?: PaypalPlanConfig;
  usd?: UsdDisplayPrices;
  [k: string]: unknown;
};

export const PLAN_PRICE_KEYS = [
  { plan: 'MEMBER' as const, interval: 'MONTHLY' as const, field: 'memberMonthlyPriceId' as const },
  { plan: 'MEMBER' as const, interval: 'ANNUAL' as const, field: 'memberAnnualPriceId' as const },
  { plan: 'PRO' as const, interval: 'MONTHLY' as const, field: 'proMonthlyPriceId' as const },
  { plan: 'PRO' as const, interval: 'ANNUAL' as const, field: 'proAnnualPriceId' as const },
  { plan: 'TEAM' as const, interval: 'ANNUAL' as const, field: 'teamAnnualPriceId' as const },
];

export function priceIdFor(
  cfg: BillingPricesConfig,
  plan: SubscriptionPlan,
  interval: SubscriptionInterval,
): string | null {
  const k = PLAN_PRICE_KEYS.find((p) => p.plan === plan && p.interval === interval);
  return (k && cfg.stripe?.[k.field]) || null;
}

export function planFromPriceId(
  cfg: BillingPricesConfig,
  priceId: string,
): { plan: SubscriptionPlan; interval: SubscriptionInterval } | null {
  const k = PLAN_PRICE_KEYS.find((p) => cfg.stripe?.[p.field] === priceId);
  if (k) return { plan: k.plan, interval: k.interval };
  // ID-241: the UAE Team seat price is a monthly Team plan. Kept out of PLAN_PRICE_KEYS so the pricing popup's
  // TEAM/MONTHLY can never pick it up — it is only sold through an AM's seat link.
  if (isUaeTeamPriceId(cfg, priceId)) return { plan: 'TEAM', interval: 'MONTHLY' };
  // Grandfathering: an old Pro price id (kept alive after a reprice) still
  // resolves to Pro so existing subscribers don't lose access. Legacy Pro is
  // annual — only the annual price is repriced; Pro monthly is unchanged.
  if (cfg.stripe?.legacyProPriceIds?.includes(priceId)) return { plan: 'PRO', interval: 'ANNUAL' };
  return null;
}

const PAYPAL_PLAN_KEYS = [
  { plan: 'MEMBER' as const, interval: 'MONTHLY' as const, field: 'memberMonthlyPlanId' as const },
  { plan: 'MEMBER' as const, interval: 'ANNUAL' as const, field: 'memberAnnualPlanId' as const },
  { plan: 'PRO' as const, interval: 'MONTHLY' as const, field: 'proMonthlyPlanId' as const },
  { plan: 'PRO' as const, interval: 'ANNUAL' as const, field: 'proAnnualPlanId' as const },
  { plan: 'TEAM' as const, interval: 'ANNUAL' as const, field: 'teamAnnualPlanId' as const },
];

export function planFromPaypalPlanId(
  cfg: BillingPricesConfig,
  planId: string,
): { plan: SubscriptionPlan; interval: SubscriptionInterval } | null {
  const k = PAYPAL_PLAN_KEYS.find((p) => cfg.paypal?.[p.field] === planId);
  if (k) return { plan: k.plan, interval: k.interval };
  // Grandfathering: an old Pro plan id kept active after a reprice → Pro (annual).
  if (cfg.paypal?.legacyProPlanIds?.includes(planId)) return { plan: 'PRO', interval: 'ANNUAL' };
  return null;
}

export function teamExtraSeatPriceId(cfg: BillingPricesConfig): string | null {
  return cfg.stripe?.teamExtraSeatAnnualPriceId || null;
}

const USD_PRICE_KEYS = {
  MEMBER: { MONTHLY: 'memberMonthly', ANNUAL: 'memberAnnual' },
  PRO: { MONTHLY: 'proMonthly', ANNUAL: 'proAnnual' },
  TEAM: { MONTHLY: null, ANNUAL: 'teamAnnual' },
} as const;

// USD package price (AppSettings.billingPrices.usd) — what the buyer saw on the
// pricing card. Used as the conversion value for ad-platform Purchase events.
export function usdAmountFor(
  cfg: BillingPricesConfig,
  plan: string | null | undefined,
  interval: string | null | undefined,
): number | null {
  const byInterval = USD_PRICE_KEYS[plan as keyof typeof USD_PRICE_KEYS];
  const key = byInterval?.[interval as keyof typeof byInterval];
  const amount = key ? cfg.usd?.[key] : undefined;
  return typeof amount === 'number' && amount > 0 ? amount : null;
}

export function uaeTeamSeatPriceId(cfg: BillingPricesConfig): string | null {
  return cfg.stripe?.uaeTeamSeatMonthlyPriceId || null;
}

export function isUaeTeamPriceId(cfg: BillingPricesConfig, priceId: string | null | undefined): boolean {
  const id = uaeTeamSeatPriceId(cfg);
  return !!id && id === priceId;
}

// Per-seat display price (USD), falling back to the plan default.
export function uaeTeamSeatUsd(cfg: BillingPricesConfig): number {
  const v = cfg.usd?.uaeTeamSeatMonthly;
  return typeof v === 'number' && v > 0 ? v : UAE_TEAM_SEAT_USD_DEFAULT;
}
